import { css, keyframes } from "@emotion/react";
import { BrowserMultiFormatReader, IScannerControls } from "@zxing/browser";
import { NotFoundException } from "@zxing/library";
import { useEffect, useRef } from "react";

type Props = {
  onScan: (value: string) => void;
};

export const BarcodeScannerBox: React.FC<Props> = ({ onScan }) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const controlsRef = useRef<IScannerControls | null>(null);
  const codeReader = useRef(new BrowserMultiFormatReader());
  const lastScanned = useRef<string | null>(null);
  const lastScanTime = useRef<number>(0);
  const onScanRef = useRef(onScan);

  useEffect(() => {
    onScanRef.current = onScan;
  }, [onScan]);

  useEffect(() => {
    const reader = codeReader.current;

    const start = async () => {
      try {
        const devices = await BrowserMultiFormatReader.listVideoInputDevices();
        if (devices.length === 0) return;

        const controls = await reader.decodeFromVideoDevice(undefined, videoRef.current!, (result, err) => {
          if (result) {
            const code = result.getText();
            const now = Date.now();
            if (code !== lastScanned.current || now - lastScanTime.current > 3000) {
              lastScanned.current = code;
              lastScanTime.current = now;
              onScanRef.current(code);
            }
          }
        });

        controlsRef.current = controls;
      } catch {
        // Camera unavailable or permission denied — silently fail.
      }
    };

    start();

    return () => {
      controlsRef.current?.stop();
      const stream = videoRef.current?.srcObject as MediaStream | null;
      stream?.getTracks().forEach((track) => track.stop());
    };
  }, []);

  return (
    <div css={wrapper}>
      <video ref={videoRef} style={{ width: "100%", height: "100%", objectFit: "cover" }} muted playsInline autoPlay />
      <div css={overlay} />
      <div css={scanArea}>
        <div css={corners}>
          <span className="tl" />
          <span className="tr" />
          <span className="bl" />
          <span className="br" />
        </div>
        <div css={scanLine} />
      </div>
    </div>
  );
};

const wrapper = css({
  width: "100%",
  aspectRatio: "1 / 1",
  position: "relative",
  overflow: "hidden",
  borderRadius: "0.75rem",
});

const overlay = css({
  position: "absolute",
  inset: 0,
  backgroundColor: "rgba(0, 0, 0, 0.6)",
  pointerEvents: "none",
  maskImage: `
    linear-gradient(black, black),
    linear-gradient(white, white)
  `,
  maskComposite: "exclude",
  WebkitMaskImage: `
    linear-gradient(black, black),
    linear-gradient(white, white)
  `,
  WebkitMaskComposite: "destination-out",
  maskPosition: "center",
  maskSize: "100% 100%, 80% 36%",
  WebkitMaskPosition: "center",
  WebkitMaskSize: "100% 100%, 80% 36%",
  maskRepeat: "no-repeat",
  WebkitMaskRepeat: "no-repeat",
  zIndex: 1,
});

const scanArea = css({
  position: "absolute",
  top: "32%",
  left: "10%",
  width: "80%",
  height: "36%",
  zIndex: 2,
  pointerEvents: "none",
});

const corners = css({
  position: "absolute",
  inset: 0,
  "& > span": {
    position: "absolute",
    width: 24,
    height: 24,
    border: "3px solid white",
  },
  "& .tl": { top: 0, left: 0, borderRight: "none", borderBottom: "none" },
  "& .tr": { top: 0, right: 0, borderLeft: "none", borderBottom: "none" },
  "& .bl": { bottom: 0, left: 0, borderRight: "none", borderTop: "none" },
  "& .br": { bottom: 0, right: 0, borderLeft: "none", borderTop: "none" },
});

const anim = keyframes`
  0% { top: 0% }
  50% { top: calc(100% - 1px) }
  100% { top: 0% }
`;

const scanLine = css({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  height: "1px",
  backgroundColor: "red",
  animation: `${anim} 2.5s ease-in-out infinite`,
});
